// @ts-nocheck
/**
 * Edit Notification — GET pre-populates from resp.data ?? resp; PATCH via
 * patchApiCall; SUPERADMIN + SUB_SUPERADMIN gate on "Is General" checkbox.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall } from '@/service/httpCall';
import { patchApiCall } from '@/service/patchApiCall';
import { API_NOTIFICATION_SINGLE, API_NOTIFICATION_EDIT } from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, Loader2, Save, Megaphone } from 'lucide-react';

const NotificationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const currentRole = getUser()?.role;
  const canSetGeneral =
    currentRole === USER_ROLES.SUPERADMIN ||
    currentRole === USER_ROLES.SUB_SUPERADMIN;

  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [isGeneral, setIsGeneral] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({ type: '', description: '' });

  useEffect(() => {
    setFetching(true);
    getApiCall(API_NOTIFICATION_SINGLE.replace(':id', id))
      .then((resp: any) => {
        const data = resp?.data || resp;
        setType(data.notification_type || '');
        setDescription(data.description || '');
        setIsGeneral(data.is_general || false);
      })
      .catch((err: any) => {
        if (typeof err === 'string' && err.trim())
          toast.error(friendlyApiError(err) || 'Failed to load notification');
      })
      .finally(() => setFetching(false));
  }, [id]);

  const validate = () => {
    const newErrors = { type: '', description: '' };
    let isValid = true;
    if (!type.trim()) {
      newErrors.type = 'Notification Type is required';
      isValid = false;
    }
    if (!description.trim()) {
      newErrors.description = 'Description is required';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const submitForm = () => {
    if (!validate()) return;
    setLoading(true);
    patchApiCall(API_NOTIFICATION_EDIT.replace(':id', id), {
      notification_type: type,
      description,
      is_general: isGeneral,
    })
      .then(() => {
        toast.success('Notification updated');
        navigate(`/${ROUTES.NOTIFICATION.LIST}`);
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Update unsuccessful');
      })
      .finally(() => setLoading(false));
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Edit Notification"
        description="Update the notification's title, description, or general flag."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.NOTIFICATION.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="mb-4 flex items-center gap-3 border-b border-border/50 pb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/25">
            <Megaphone className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">Update notification</h3>
            <p className="text-[11px] text-muted-foreground">
              Existing recipients keep their assignment.
            </p>
          </div>
        </div>

        {fetching ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading notification…
          </div>
        ) : (
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="title" className="text-xs font-semibold text-foreground">
                  Title <span className="text-destructive">*</span>
                </label>
                <input
                  id="title"
                  type="text"
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    setErrors((prev) => ({ ...prev, type: '' }));
                  }}
                  className={`input-embossed h-10 w-full rounded-md px-3 text-sm ${
                    errors.type ? 'ring-2 ring-destructive/40' : ''
                  }`}
                />
                {errors.type && (
                  <p className="text-[11px] text-destructive">{errors.type}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="description"
                  className="text-xs font-semibold text-foreground"
                >
                  Description <span className="text-destructive">*</span>
                </label>
                <textarea
                  id="description"
                  rows={4}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setErrors((prev) => ({ ...prev, description: '' }));
                  }}
                  className={`input-embossed w-full rounded-md p-3 text-sm ${
                    errors.description ? 'ring-2 ring-destructive/40' : ''
                  }`}
                />
                {errors.description && (
                  <p className="text-[11px] text-destructive">{errors.description}</p>
                )}
              </div>

              {canSetGeneral && (
                <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
                  <label className="flex cursor-pointer items-center gap-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isGeneral}
                      onClick={() => setIsGeneral(!isGeneral)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                        isGeneral ? 'bg-emerald-500' : 'bg-muted/70'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                          isGeneral ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                    <div>
                      <div className="text-xs font-semibold text-foreground">
                        Is General Notification
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        General notifications are broadcast to all recipients.
                      </div>
                    </div>
                  </label>
                </div>
              )}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
              <Button asChild variant="outline">
                <Link to={`/${ROUTES.NOTIFICATION.LIST}`}>Cancel</Link>
              </Button>
              <Button
                onClick={submitForm}
                type="button"
                disabled={loading}
                className="gap-1.5"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {loading ? 'Updating…' : 'Save changes'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </ScrollPage>
  );
};

export default NotificationEdit;
